import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';
import { FiBriefcase, FiArrowRight, FiCheckCircle, FiShield, FiUpload, FiImage, FiLoader } from 'react-icons/fi';

const Login = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const redirect = searchParams.get('redirect');
  
  const [isLogin, setIsLogin] = useState(mode !== 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Storage for selected logo
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Spam protection state for registration
  const [spamChallenge, setSpamChallenge] = useState({ a: 0, b: 0 });
  const [userSpamAnswer, setUserSpamAnswer] = useState('');

  const { signIn, signUp, uploadLogo } = useAppContext();
  const navigate = useNavigate();

  const generateChallenge = useCallback(() => {
    setSpamChallenge({
      a: Math.floor(Math.random() * 10) + 1,
      b: Math.floor(Math.random() * 10) + 1
    });
    setUserSpamAnswer('');
  }, []);

  useEffect(() => {
    if (mode === 'signup') {
      setIsLogin(false);
    } else if (mode === 'login') {
      setIsLogin(true);
    }
  }, [mode]);

  useEffect(() => {
    if (!isLogin) {
      generateChallenge();
    }
  }, [isLogin, generateChallenge]);

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || (!isLogin && !orgName)) {
      setError('Please fill in all fields');
      return;
    }

    // Spam protection check for registration
    if (!isLogin) {
      const correctAnswer = spamChallenge.a + spamChallenge.b;
      if (parseInt(userSpamAnswer) !== correctAnswer) {
        setError('Incorrect verification answer. Please try again.');
        generateChallenge();
        return;
      }
    }
    
    setIsSubmitting(true);
    setError('');
    setSuccessMsg('');
    
    try {
      if (isLogin) {
        const result = await signIn(email, password);
        if (result.success) {
          await new Promise(resolve => setTimeout(resolve, 100));
          navigate(redirect || '/employer/dashboard');
        } else {
          setError(result.error);
          setIsSubmitting(false);
        }
      } else {
        let uploadedLogoUrl = null;
        
        // 1. Upload logo first if selected, using a temp ID
        if (selectedLogo) {
          const tempId = `reg-${Date.now()}`;
          const uploadResult = await uploadLogo(selectedLogo, tempId);
          if (uploadResult.success) {
            uploadedLogoUrl = uploadResult.url;
          }
        }

        // 2. Perform SignUp with the logo URL in metadata
        const result = await signUp(email, password, orgName, uploadedLogoUrl, honeypot);
        
        if (result.success) {
          if (result.needsConfirmation) {
            setSuccessMsg('Account created! Please check your email to confirm your account.');
          } else {
            await new Promise(resolve => setTimeout(resolve, 100));
            navigate(redirect || '/employer/dashboard');
          }
        } else {
          setError(result.error);
        }
        setIsSubmitting(false);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 bg-stone-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="bg-emerald-100 p-3 rounded-full">
            <SafeIcon icon={FiBriefcase} className="text-3xl text-emerald-600" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-stone-900">
          {isLogin ? 'Sign in to your account' : 'Create an employer account'}
        </h2>
        <p className="mt-2 text-center text-sm text-stone-600">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setSuccessMsg('');
            }}
            className="font-medium text-emerald-600 hover:text-emerald-500"
          >
            {isLogin ? 'Sign up' : 'Sign in here'}
          </button>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-stone-200">
          {successMsg ? (
            <div className="text-center space-y-6">
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-4 rounded-md flex flex-col items-center">
                <SafeIcon icon={FiCheckCircle} className="text-3xl mb-3 text-emerald-500" />
                <p className="font-medium">{successMsg}</p>
              </div>
              
              <button
                onClick={() => {
                  setSuccessMsg('');
                  setIsLogin(true);
                }}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-stone-300 rounded-md shadow-sm text-sm font-medium text-stone-700 bg-white hover:bg-stone-50 transition-colors"
              >
                Return to Sign In
              </button>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm p-3 rounded-md">
                  {error}
                </div>
              )}
              
              {!isLogin && (
                <>
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-stone-700">Organization Logo (Optional)</label>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="relative group cursor-pointer"
                    >
                      <div className="h-32 w-full bg-stone-50 rounded-2xl flex items-center justify-center overflow-hidden border-2 border-dashed border-stone-200 group-hover:border-emerald-500 transition-all p-4">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo Preview" className="max-h-full w-auto object-contain" />
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <SafeIcon icon={FiImage} className="text-3xl text-stone-300" />
                            <span className="text-[10px] font-black uppercase text-stone-400 tracking-widest text-center">Click to upload brand logo</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-emerald-600/0 group-hover:bg-emerald-600/5 transition-all flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 bg-white p-2 rounded-full shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-all">
                            <SafeIcon icon={FiUpload} className="text-emerald-600" />
                          </div>
                        </div>
                      </div>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleLogoSelect} 
                        className="hidden" 
                        accept="image/*" 
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700">Employer Name</label>
                    <div className="mt-1">
                      <input
                        type="text"
                        required
                        className="appearance-none block w-full px-3 py-2 border border-stone-300 rounded-md shadow-sm placeholder-stone-400 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        placeholder="e.g. Adventure Tours LLC"
                      />
                    </div>
                  </div>

                  {/* Honeypot field for registration */}
                  <input 
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={e => setHoneypot(e.target.value)}
                    style={{ display: 'none !important' }}
                    className="hidden"
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-stone-700">Email address</label>
                <div className="mt-1">
                  <input
                    type="email"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-stone-300 rounded-md shadow-sm placeholder-stone-400 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700">Password</label>
                <div className="mt-1">
                  <input
                    type="password"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-stone-300 rounded-md shadow-sm placeholder-stone-400 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              {!isLogin && (
                <div className="p-4 bg-stone-50 rounded-lg border border-stone-200 space-y-4">
                  <div className="flex items-center gap-2 text-stone-900 font-bold text-xs uppercase tracking-wider">
                    <SafeIcon icon={FiShield} className="text-emerald-500" />
                    <h4>Spam Protection</h4>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm font-black text-emerald-600">
                      {spamChallenge.a} + {spamChallenge.b} =
                    </div>
                    <input
                      type="number"
                      required
                      placeholder="?"
                      className="w-20 px-3 py-2 border border-stone-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                      value={userSpamAnswer}
                      onChange={e => setUserSpamAnswer(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {isLogin && (
                <div className="flex items-center justify-end">
                  <div className="text-sm">
                    <Link to="/employer/forgot-password" size="sm" className="font-medium text-emerald-600 hover:text-emerald-500">
                      Forgot your password?
                    </Link>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors disabled:bg-stone-400"
                >
                  {isSubmitting ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
                  {!isSubmitting && <SafeIcon icon={FiArrowRight} className="ml-2" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;